import React, { useState } from 'react';
import {
  TrendingUp, DollarSign, Package, Users, Calendar, Download,
  ArrowUpRight, ArrowDownRight, BarChart3, PieChart
} from 'lucide-react';

// Mock report data
const summaryStats = {
  revenue: { value: 2400000, change: 23, trend: 'up' },
  shipments: { value: 469, change: 15, trend: 'up' },
  customers: { value: 5, change: 2, trend: 'up' },
  avgValue: { value: 5117, change: -3, trend: 'down' },
};

const monthlyRevenue = [
  { month: 'Jul', revenue: 1200000 },
  { month: 'Aug', revenue: 1450000 },
  { month: 'Sep', revenue: 1680000 },
  { month: 'Oct', revenue: 1920000 },
  { month: 'Nov', revenue: 2150000 },
  { month: 'Dec', revenue: 2400000 },
];

const shipmentsByStatus = [
  { status: 'Delivered', count: 342, percentage: 73, color: 'lime' },
  { status: 'In Transit', count: 85, percentage: 18, color: 'blue' },
  { status: 'Ready', count: 28, percentage: 6, color: 'cyan' },
  { status: 'Pending', count: 14, percentage: 3, color: 'amber' },
];

const topRoutes = [
  { route: 'Miami → Kingston', shipments: 245, revenue: 1250000 },
  { route: 'Miami → Montego Bay', shipments: 128, revenue: 680000 },
  { route: 'Orlando → Kingston', shipments: 56, revenue: 298000 },
  { route: 'Miami → Portmore', shipments: 40, revenue: 172000 },
];

export default function TenantReports() {
  const [dateRange, setDateRange] = useState('month');

  const maxRevenue = Math.max(...monthlyRevenue.map(m => m.revenue));

  return (
    <div className="tenant-reports-page" data-testid="tenant-reports">
      {/* Header with Date Range */}
      <div className="reports-header">
        <div className="date-range-selector">
          <button 
            className={`range-btn ${dateRange === 'week' ? 'active' : ''}`}
            onClick={() => setDateRange('week')}
          >
            Week
          </button>
          <button 
            className={`range-btn ${dateRange === 'month' ? 'active' : ''}`}
            onClick={() => setDateRange('month')}
          >
            Month
          </button>
          <button 
            className={`range-btn ${dateRange === 'quarter' ? 'active' : ''}`}
            onClick={() => setDateRange('quarter')}
          >
            Quarter
          </button>
          <button 
            className={`range-btn ${dateRange === 'year' ? 'active' : ''}`}
            onClick={() => setDateRange('year')}
          >
            Year
          </button>
        </div>
        
        <button className="btn btn-secondary" data-testid="export-report">
          <Download size={14} /> Export Report
        </button>
      </div>

      {/* Summary Stats */}
      <div className="reports-stats-grid">
        <div className="report-stat-card">
          <div className="stat-header">
            <DollarSign size={20} />
            <span className={`stat-change ${summaryStats.revenue.trend}`}>
              {summaryStats.revenue.trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {summaryStats.revenue.change}%
            </span>
          </div>
          <span className="stat-value">JMD {(summaryStats.revenue.value / 1000000).toFixed(1)}M</span>
          <span className="stat-label">Total Revenue</span>
        </div>
        
        <div className="report-stat-card">
          <div className="stat-header">
            <Package size={20} />
            <span className={`stat-change ${summaryStats.shipments.trend}`}>
              {summaryStats.shipments.trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {summaryStats.shipments.change}%
            </span>
          </div>
          <span className="stat-value">{summaryStats.shipments.value}</span>
          <span className="stat-label">Total Shipments</span>
        </div>
        
        <div className="report-stat-card">
          <div className="stat-header">
            <Users size={20} />
            <span className={`stat-change ${summaryStats.customers.trend}`}>
              {summaryStats.customers.trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              +{summaryStats.customers.change}
            </span>
          </div>
          <span className="stat-value">{summaryStats.customers.value}</span>
          <span className="stat-label">Active Customers</span>
        </div>
        
        <div className="report-stat-card">
          <div className="stat-header">
            <TrendingUp size={20} />
            <span className={`stat-change ${summaryStats.avgValue.trend}`}>
              {summaryStats.avgValue.trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {summaryStats.avgValue.change}%
            </span>
          </div>
          <span className="stat-value">JMD {summaryStats.avgValue.value.toLocaleString()}</span>
          <span className="stat-label">Avg Shipment Value</span>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="reports-charts-grid">
        {/* Revenue Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Revenue Trend</h3>
            <BarChart3 size={20} />
          </div>
          <div className="bar-chart">
            {monthlyRevenue.map((item, index) => (
              <div key={index} className="bar-item">
                <div 
                  className="bar" 
                  style={{ height: `${(item.revenue / maxRevenue) * 100}%` }}
                >
                  <span className="bar-value">JMD {(item.revenue / 1000000).toFixed(1)}M</span>
                </div>
                <span className="bar-label">{item.month}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Shipments by Status */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Shipments by Status</h3>
            <PieChart size={20} />
          </div>
          <div className="status-breakdown">
            {shipmentsByStatus.map((item, index) => (
              <div key={index} className="status-row">
                <div className="status-info">
                  <div className={`status-dot ${item.color}`}></div>
                  <span className="status-name">{item.status}</span>
                </div>
                <div className="status-bar-container">
                  <div 
                    className={`status-bar ${item.color}`}
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
                <span className="status-count">{item.count}</span>
                <span className="status-percentage">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Routes */}
      <div className="top-routes-card">
        <div className="card-header">
          <h3>Top Routes</h3>
        </div>
        <div className="routes-table">
          <div className="table-header">
            <span>Route</span>
            <span>Shipments</span>
            <span>Revenue</span>
            <span>% of Total</span>
          </div>
          {topRoutes.map((route, index) => (
            <div key={index} className="table-row" data-testid={`route-${index}`}>
              <span className="route-name">{route.route}</span>
              <span className="route-shipments">{route.shipments}</span>
              <span className="route-revenue">JMD {(route.revenue / 1000).toFixed(0)}K</span>
              <span className="route-percentage">
                {((route.revenue / summaryStats.revenue.value) * 100).toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
